import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { HorizonDeck } from './components/HorizonDeck';
import { VoiceLink } from './components/VoiceLink';
import * as mm from 'music-metadata-browser';

// Environment Registry
const ENVIRONMENTS = {
    cyberpunk: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/shanghai_bund_1k.hdr',
    mars: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/moonless_golf_1k.hdr',
    studio: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_09_1k.hdr',
};

function SpatialApp() {
    const [currentEnv, setCurrentEnv] = useState('cyberpunk');
    const [currentVideo, setCurrentVideo] = useState<string | null>(null);
    const [currentYoutubeId, setCurrentYoutubeId] = useState<string | null>(null);
    const [showGrid, setShowGrid] = useState(true);
    const [messages, setMessages] = useState<any[]>([]);

    // Audio State
    const [currentAudio, setCurrentAudio] = useState<string | null>(null);

    // Thread Safe Message Adder
    const addMessage = (role: 'user' | 'assistant', text: string) => {
        setMessages(prev => [...prev, { id: Date.now(), role, text }]);
    };

    // Environment/Grid Logic
    const switchEnv = (env) => {
        console.log(`[Spatial OS] Switching to ${env}`);
        setCurrentEnv(env);
        setCurrentVideo(null); // Clear video 
        setCurrentYoutubeId(null);
        setShowGrid(true); // Grid ON
    };

    const switchVideo = (videoPath, youtubeId = null) => {
        console.log(`[Spatial OS] Loading Video ${videoPath} | YT: ${youtubeId}`);
        if (videoPath) setCurrentVideo(videoPath);
        if (youtubeId) setCurrentYoutubeId(youtubeId);
        setShowGrid(false); // Grid OFF
    };

    // Action Handler (From Router/Brain)
    const handleAction = (action: any) => {
        if (action.type === 'SET_ENV') {
            switchEnv(action.value);
        }
        if (action.type === 'SET_MODE' && action.mode === 'video') {
            switchVideo(action.url, action.youtubeId);
        }
        if (action.type === 'SET_GRID') {
            setShowGrid(action.value);
        }
    };

    const handleMessage = (role: string, text: string) => {
        addMessage(role as any, text);
    }

    const envUrl = ENVIRONMENTS[currentEnv];
    const themeColor = currentEnv === 'mars' ? '#ff3300' : '#00f3ff';

    // ------------------------------------------------------------------
    // VISUAL RAG SYSTEM
    // ------------------------------------------------------------------
    const triggerVisuals = async (filename: string, lyrics?: string) => {
        const cleanName = filename.replace(/\.[^/.]+$/, "").replace(/_/g, " ");
        let prompt = `Generate visuals for song: "${cleanName}"`;

        if (lyrics) {
            // If we found lyrics, use the first few lines as context
            const snippet = lyrics.substring(0, 100).replace(/\n/g, " ");
            prompt += `. Lyrics vibe: ${snippet}`;
            addMessage('user', `[System] Lyrics Detected: "${snippet}..."`);
        } else {
            addMessage('user', `[System] Analysis: ${cleanName}`);
        }

        try {
            // Ask the Brain (Router) for a Pexels Video URL
            const response = await fetch("https://brain.gni-ai-solutions.com/api/voice-code", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-gni-key': "gni-sovereign-secret" },
                body: JSON.stringify({ prompt: prompt, session_id: "spatial-deck-audio", source: "text" })
            });
            const data = await response.json();

            if (data.action) {
                handleAction(data.action);
                addMessage('assistant', "Visuals synced to audio.");
            } else {
                addMessage('assistant', "No matching visuals found.");
            }
        } catch (e) {
            console.error("Visual RAG Failed", e);
        }
    }

    // ------------------------------------------------------------------
    // DRAG AND DROP HANDLER
    // ------------------------------------------------------------------
    useEffect(() => {
        const handleDrop = async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
                const file = e.dataTransfer.files[0];
                console.log("File Dropped:", file.name);

                if (file.type.startsWith('video/')) {
                    const url = URL.createObjectURL(file);
                    switchVideo(url);
                    addMessage('assistant', `Loading local video: ${file.name}`);
                }
                else if (file.type.startsWith('audio/')) {
                    const url = URL.createObjectURL(file);
                    setCurrentAudio(url);

                    // Attempt Metadata Extraction (Lyrics)
                    try {
                        const metadata = await mm.parseBlob(file);
                        const lyrics = metadata.common.lyrics && metadata.common.lyrics.length > 0
                            ? metadata.common.lyrics[0]
                            : undefined;

                        triggerVisuals(file.name, lyrics);

                    } catch (err) {
                        console.warn("Metadata extraction failed, falling back to filename", err);
                        triggerVisuals(file.name);
                    }
                }
                else {
                    addMessage('assistant', "I only support Video (MP4) or Audio (MP3) files.");
                }
            }
        };

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        window.addEventListener('drop', handleDrop);
        window.addEventListener('dragover', handleDragOver);

        return () => {
            window.removeEventListener('drop', handleDrop);
            window.removeEventListener('dragover', handleDragOver);
        };
    }, []);

    return (
        <>
            <HorizonDeck
                envUrl={envUrl}
                videoUrl={currentVideo}
                youtubeId={currentYoutubeId}
                themeColor={themeColor}
                preset={currentEnv === 'cyberpunk' ? 'city' : undefined}
                messages={messages}
                showGrid={showGrid}
            />
            <VoiceLink
                onAction={handleAction}
                onMessage={handleMessage}
            />

            {/* HUD / CONTROLS OVERLAY */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                <button onClick={() => switchEnv('cyberpunk')} style={hudBtn}>🏙️ CYBERPUNK</button>
                <button onClick={() => switchEnv('mars')} style={{ ...hudBtn, color: '#ff3300', borderColor: '#ff3300' }}>🪐 MARS</button>
                <button onClick={() => switchVideo('/assets/video/matrix.mp4')} style={{ ...hudBtn, color: '#00ff00', borderColor: '#00ff00' }}>💊 MATRIX</button>
                <button onClick={() => switchVideo('/assets/video/lofi.mp4')} style={{ ...hudBtn, color: '#ff99cc', borderColor: '#ff99cc' }}>🎧 LOFI</button>
                <button onClick={() => switchVideo('/assets/video/synthwave.mp4')} style={{ ...hudBtn, color: '#cc00ff', borderColor: '#cc00ff' }}>🌆 SYNTH</button>

                <div style={{ marginTop: '20px', padding: '10px', border: '1px dashed #555', color: '#888', fontSize: '10px', textAlign: 'center' }}>
                    DRAG MP4 / MP3 HERE
                </div>
            </div>

            {/* Hidden Audio Player */}
            {currentAudio && (
                <audio src={currentAudio} autoPlay loop />
            )}
        </>
    );
}

const hudBtn = {
    padding: '10px',
    background: 'rgba(0,0,0,0.5)',
    color: '#00f3ff',
    border: '1px solid #00f3ff',
    cursor: 'pointer'
};

// Mount Function
export function mountSpatialOS(container: HTMLElement) {
    const root = createRoot(container);
    root.render(<SpatialApp />);
}
